import type { GenerateZkProofRequest } from '@modules/docs-verification-widget/api/requests/generate-zk-proof-request';
import type { GetVerificationResponse } from '@modules/docs-verification-widget/models/get-verification-response';
import type { GetZkProofResponse } from '@modules/docs-verification-widget/models/get-zk-proof-response';
import type { UploadDocumentResponse } from '@modules/docs-verification-widget/models/upload-document-response';
import i18n from 'i18next';

import type {
    ProceedVerificationRequest,
    SetCountryRequest,
    UploadDocumentRequest,
    UploadLivenessDocumentRequest,
} from './requests';
import { sdkApi } from './sdk-base-api';

export const sdkApiSlice = sdkApi.injectEndpoints({
    endpoints: (build) => ({
        getVerificationById: build.query<GetVerificationResponse, { url: string; id: string }>({
            query: ({ url, id }) => `${url}/api/v3/sdk/${id}`,
        }),
        getVerificationByIdStatus: build.query<
            { verification_is_done: boolean },
            { url: string; id: string }
        >({
            query: ({ url, id }) => `${url}/api/v4/verifications/${id}/status`,
        }),
        uploadDocumentSDK: build.mutation<UploadDocumentResponse, UploadDocumentRequest>({
            query({ url, id, body }) {
                const formData = new FormData();

                Object.keys(body).forEach((key) => {
                    const value = body[key as keyof typeof body];

                    if (value !== undefined) {
                        formData.append(key, value);
                    }
                });

                return {
                    url: `${url}/api/v3/upload/sdk/${id}`,
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Accept-Language': i18n.language,
                    },
                };
            },
            invalidatesTags: [],
        }),
        uploadLivenessDocumentSDK: build.mutation<
            UploadDocumentResponse,
            UploadLivenessDocumentRequest
        >({
            query({ url, id, body }) {
                const formData = new FormData();

                formData.append(
                    'metadata',
                    JSON.stringify({
                        frames: body.files.map((item, index) => {
                            const frameId = `frame${index + 1}`;

                            formData.append(frameId, item);

                            return {
                                frame_id: frameId,
                                timestamp: new Date(item.lastModified).getTime(),
                            };
                        }),
                    }),
                );

                return {
                    url: `${url}/api/v4/upload/sdk/${id}/liveness-batch`,
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Accept-Language': i18n.language,
                    },
                };
            },
            invalidatesTags: [],
        }),
        proceedVerificationSDK: build.mutation<
            { result: string; error?: string },
            ProceedVerificationRequest
        >({
            query({ url, id }) {
                return {
                    url: `${url}/api/v3/sdk/${id}/proceed`,
                    method: 'POST',
                    headers: {
                        'Accept-Language': i18n.language,
                    },
                };
            },
            invalidatesTags: [],
        }),
        setCountrySDK: build.mutation<{ message: string }, SetCountryRequest>({
            query({ url, id, country }) {
                return {
                    url: `${url}/api/v3/sdk/${id}/set_country`,
                    method: 'POST',
                    body: {
                        country,
                    },
                    headers: {
                        'Accept-Language': i18n.language,
                    },
                };
            },
            invalidatesTags: [],
        }),
        generateZkProof: build.mutation<GetZkProofResponse, GenerateZkProofRequest>({
            query({ body, url }) {
                return {
                    url: `${url}/api/v4/web3/zkproof/generate`,
                    method: 'POST',
                    body,
                    headers: {
                        'Accept-Language': i18n.language,
                    },
                };
            },
            invalidatesTags: [],
        }),
        setManualFieldsSDK: build.mutation<
            { message: string },
            { url: string; id: string; body: unknown }
        >({
            query({ url, id, body }) {
                return {
                    url: `${url}/api/v3/sdk/${id}/fields`,
                    method: 'POST',
                    body,
                    headers: {
                        'Accept-Language': i18n.language,
                    },
                };
            },
        }),
        uploadCustomFileSDK: build.mutation<
            { message: string },
            { url: string; id: string; body: FormData }
        >({
            query({ url, id, body }) {
                return {
                    url: `${url}/api/v3/sdk/${id}/upload`,
                    method: 'POST',
                    body,
                    headers: {
                        'Accept-Language': i18n.language,
                    },
                };
            },
        }),
        uploadFingerprintSDK: build.mutation<
            { message: string },
            {
                url: string;
                identifier: string;
                shortVerificationId: string;
                [key: string]: unknown;
            }
        >({
            query({ url, ...rest }) {
                return {
                    url: `${url}/api/v4/device-intelligence/collect`,
                    method: 'POST',
                    body: rest,
                };
            },
        }),
    }),
});

export const {
    useUploadDocumentSDKMutation,
    useProceedVerificationSDKMutation,
    useLazyGetVerificationByIdQuery,
    useLazyGetVerificationByIdStatusQuery,
    useSetCountrySDKMutation,
    useSetManualFieldsSDKMutation,
    useUploadCustomFileSDKMutation,
    useUploadFingerprintSDKMutation,
    useGenerateZkProofMutation,
    useUploadLivenessDocumentSDKMutation,
} = sdkApiSlice;
